import React from "react";
import { Route, Routes, useSearchParams } from "react-router-dom";
import { Navigate } from "react-router";
import { createTheme, ThemeProvider, useMediaQuery, useTheme } from "@mui/material";
import Header from "components/Header";
import JoyfulSetting from "features/joyful/routes/JoyfulSetting";
import ProductSetting from "features/joyful/routes/ProductSetting";
import Changelog from "features/change_log/route/Changelog";
import MstTireList from "features/mst_store/routes/TireList";
import MstTireBrand from "features/mst_store/routes/TireBrand";
import Forbidden from "components/errors/Forrbiden";
import CsvList from "features/mst_csv/routes/CsvList";
import ProcFee from "features/proc_fee/routes/ProcFeeSetting";
import ItemPriceSetting from "features/proc_fee/routes/ItemPriceSetting";
import WheelList from "features/mst_wheel/routes/WheelList";
import ViewQuotationPdf from "features/quotation/components/ViewQuotationPdf";
import routes from "constants/routes";
import MstTireWheelStore from "features/mst_store_tire_wheel_filter/routes/MstTireWheelStore";
import WheelTireProcFeeList from "features/mst_proc_fee/routes/WheelTireProcFeeList";
import WheelTireEstResultPdf from "features/car_model/routes/WheelTireEstResultPdf";
import WarehouseRegister from "features/supplier_warehouse/routes/WarehoseRegister";
import OrderCheckForm from "features/order/routes/OrderCheckForm";
import OrderStoreList from "features/order/routes/OrderStoreList";
import OrderReceive from "features/order/routes/OrderReceive";
import ClientStockList from "features/stock/routes/ClientStockList";
import InvalidRoutePage from "components/errors/InvalidRoutePage";
import NotFound from "components/errors/NotFound";
import RouteTitle from "components/RouteTitle";
import SMSPage from "features/report/routes/SMSPage";
import BOErrorPage from "features/report/routes/BOErrorPage";
import MstMain from "features/mst_maintenance/routes/MstMain";
import Home from "features/common/routes/Home";
import Login from "features/auth/components/Login";
import ChangePassword from "features/auth/components/ChangePassword";
import StockMenu from "features/common/routes/StockMenu";
import NewQuote from "features/new_quote/routes/NewQuote";
import NewQuoteTireCar from "features/new_quote/routes/NewQuoteTireCar";
import MasterManagement from "features/common/routes/MasterManagement";
import MstStoreList from "features/mst_store/routes/StoreList";
import MstUserList from "features/mst_user/routes/UserList";
import ItemInfoList from "features/item/routes/ItemInfoList";
import ItemDetail from "features/item/routes/ItemDetail";
import ItemRegister from "features/item/routes/ItemRegister";
import OrderList from "features/order/routes/OrderList";
import StockList from "features/stock/routes/StockList";
import StoreStockList from "features/stock/routes/StoreStockList";
import SupplierStockList from "features/stock/routes/SupplierStockList";
import ProductStock from "features/stock/routes/ProductStock";
import CompanyStockList from "features/stock/routes/CompanyStockList";
import MstProcFee from "features/mst_proc_fee/routes/ProcFeeSetting";
import ClientList from "features/mst_client/routes/ClientList";
import ShippingDestList from "features/mst_shipping_dest/routes/ShippingDestList";
import MstItem from "features/mst_item/routes/ItemList";
import MstItemPrice from "features/mst_item/routes/ItemPrice";
import ForgotPassword from "features/auth/components/ForgotPassword";
import SearchCarTiresize from "features/mst_car_tiresize/routes/Search";
import CarTypeSelector from "features/car_model/routes/CarTypeSelector";
import MstCarModelProductCode from "features/car_model/routes/ProductCode";
import ProcFeeList from "features/mst_proc_fee/routes/ProcFeeList";
import MstCarModelEstResult from "features/car_model/routes/EstResult";
import MstCarModelEstResultPdf from "features/car_model/routes/EstResultPdf";
import QuotationList from "features/quotation/routes/QuotationList";
import QuotationDetail from "features/quotation/routes/QuotationDetail";
import YHCompanyList from "features/mst_yh_company/routes/List";
import MstCategoryList from "features/mst_category/routes/List";
import MstVendorList from "features/mst_vendor/routes/VendorList";
import MstOption from "features/mst_option/routes/OptionList";
import SystemUsagePage from "features/report/routes/SystemUsagePage";
import TireReportPage from "features/report/routes/TireReportPage";
import FourBundlePriceList from "features/mst_4bundle_price_twset/routes/FourBundlePriceList";
import WheelBrand from "features/mst_wheel/routes/WheelBrand";
import { getUserType } from "util/auth";
import { USER_TYPE, USER_TYPE_LIST } from "constants/user-type";
import WheelCode from "features/mst_wheel/routes/WheelCode";
import MstWheelStore from "features/mst_store_tire_wheel_filter/routes/MstWheelStore";
import ProcFeeConfirm from "features/mst_proc_fee/routes/ProcFeeConfirm";
import OrderStoreCheckForm from "features/order/routes/OrderStoreCheckForm";
import RPACsvUpload from "features/order/routes/RPACsvUpload";
import WheelPriceSetting from "features/mst_wheel/routes/WheelPriceSetting";
import WinterWheelPriceSetting from "features/mst_wheel/routes/WinterWheelPriceSetting";
import SetTireProcFeeList from "features/mst_proc_fee/routes/SetTireProcFeeList";
import RoleBasedRoute from "routes/RoleBasedRoute";
import PrivateRoute from "routes/PrivateRoute";
import LargeCsv from "features/order/routes/LargeCsv";

/**
 * ヘッダー、フッター付きRoutes
 * @param root0 ヘッダーとフッターの間に表示する要素が入った引数
 * @returns JSX.Element
 */
function HeaderFooterRoutes({ elements }: { elements: JSX.Element[] }) {
  const currentTheme = useTheme();
  const isMobile = useMediaQuery(currentTheme.breakpoints.down("sm"));
  return (
    <div className={isMobile ? "mobile" : ""}>
      <header>
        <Header />
      </header>
      <Routes>{elements}</Routes>
      {/* <footer className="footer">
        <Footer />
      </footer> */}
    </div>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#f5d944",
      light: "#f8e374",
      dark: "#f2cf14",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ff9900",
      light: "#ffad33",
      dark: "#ccaa00",
      contrastText: "#ffffff",
    },
    action: {
      selectedOpacity: 0.35,
      hoverOpacity: 0.2,
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          color: "#ffffff",
          backgroundColor: "#ffa500",
          "&:hover": {
            backgroundColor: "#ff8c00",
          },
        },
        contained: {
          color: "black", // 追加
          backgroundColor: "#f90",
          // color: "#ffffff",
          // backgroundColor: "#ff9900",
          // textShadow: "-1px 0 1px rgba(51,51,51,0.75)",
          fontWeight: "bold",
          "&.Mui-disabled": {
            backgroundColor: "#c0c0c0",
            textShadow: "none",
          },
          "&:hover": {
            backgroundColor: "#ffad33",
          },
        },
      },
      variants: [
        {
          props: { variant: "custom-outlined" },
          style: {
            color: "black", // 追加
            // color: "#ff9900",
            border: "1px solid #ff9900",
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: "#c0c0c0",
              textShadow: "none",
            },
            "&:hover": {
              border: "1px solid #e58900",
              backgroundColor: "#fff4e5",
            },
          },
        },
        {
          props: { variant: "gray-outlined" },
          style: {
            color: "gray", // #808080
            fontWeight: "bold",
            border: "1px solid gray",
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: "#eaeaea",
              textShadow: "none",
            },
            "&:hover": {
              color: "#444",
              border: "1px solid #333",
              backgroundColor: "#eee",
            },
          },
        },
        {
          props: { variant: "selected-gray-outlined" },
          style: {
            color: "gray", // #808080
            fontWeight: "bold",
            border: "1px solid gray",
            backgroundColor: "#ffff4d",
            "&.Mui-disabled": {
              // backgroundColor: "#c0c0c0",
              backgroundColor: "#eaeaa5ff",
              textShadow: "none",
            },
            "&:hover": {
              color: "#444",
              border: "1px solid #333",
              backgroundColor: "#ffff4d",
            },
          },
        },
        {
          props: { variant: "custom-text" },
          style: {
            color: "#000000",
            backgroundColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: "#c0c0c0",
              textShadow: "none",
            },
            "&:hover": {
              backgroundColor: "#fff4e5",
            },
          },
        },
      ],
    },
  },
  typography: {
    fontFamily: [
      // "IrohakakuC",
      "Arial",
      "sans-serif",
    ].join(","),
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

const BASE_TITLE = "YHBB";

const getRoleBasedRoutes = (userType: keyof typeof USER_TYPE) => [
  {
    path: routes.stockMenu,
    element: (
      <RouteTitle title={`${BASE_TITLE} - メニュー`}>
        <StockMenu />
      </RouteTitle>
    ),
  },
  {
    path: routes.quoteList,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 見積一覧`}>
        <QuotationList />
      </RouteTitle>
    ),
  },
  {
    path: routes.newQuote,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 新規見積`}>
        <NewQuote />
      </RouteTitle>
    ),
  },
  {
    path: routes.newQuoteTireCar,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 利用者一覧`}>
        <NewQuoteTireCar />
      </RouteTitle>
    ),
  },
  {
    path: routes.masterManagement,
    element: (
      <RouteTitle title={`${BASE_TITLE}`}>
        <MasterManagement />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstStore,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 店舗一覧`}>
        <MstStoreList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstTireWheelStore,
    element: (
      <RouteTitle title={`${BASE_TITLE} - ホイールを選ぶ`}>
        <MstTireWheelStore />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstWheelStore,
    element: (
      <RouteTitle title={`${BASE_TITLE} - ホイールを選ぶ`}>
        <MstWheelStore />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstTireList,
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤ一覧`}>
        <MstTireList />
      </RouteTitle>
    ),
  },
  // { path: routes.mstTireBrand, element: <RouteTitle title={`${BASE_TITLE} - ブランド詳細 タイヤ`}><MstTireBrand /></RouteTitle> }, // removed following spec 14/3
  {
    path: routes.mstUser,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 利用者一覧`}>
        <MstUserList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstClient,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 得意先一覧`}>
        <ClientList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstShippingDest,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 発送先管理`}>
        <ShippingDestList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstProcFeeSetting,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 工賃設定`}>
        {userType === USER_TYPE_LIST.headquarters.value ? <MstProcFee /> : <ProcFee />}
      </RouteTitle>
    ),
  },
  { path: routes.procFeeSetting, element: <ProcFee /> },
  {
    path: routes.itemPriceSetting,
    element:
      userType === USER_TYPE_LIST.headquarters.value /* C0001 */ ? (
        <RouteTitle title={`${BASE_TITLE} - タイヤ売価設定`}>
          <MstTireBrand />
        </RouteTitle>
      ) : (
        <RouteTitle title={`${BASE_TITLE} - タイヤ売価設定`}>
          <ItemPriceSetting />
        </RouteTitle>
      ),
  },
  {
    path: routes.mstItem,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品一覧`}>
        <MstItem />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstItemEditPrice,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品価格`}>
        <MstItemPrice />
      </RouteTitle>
    ),
  },
  {
    path: routes.items,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品一覧`}>
        <ItemInfoList />
      </RouteTitle>
    ),
  },
  {
    path: routes.itemDetail,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 詳細`}>
        <ItemDetail />
      </RouteTitle>
    ),
  },
  {
    path: routes.itemRegister,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 新規登録`}>
        <ItemRegister />
      </RouteTitle>
    ),
  },
  {
    path: routes.quotationDetail,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 見積詳細`}>
        <QuotationDetail />
      </RouteTitle>
    ),
  },
  // {
  //   path: routes.orders,
  //   element: (
  //     <RouteTitle title={`${BASE_TITLE} - 注文一覧`}>
  //       <OrderList />
  //     </RouteTitle>
  //   ),
  // },
  {
    path: routes.mstCarTireSizeSearch,
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤサイズから選ぶ`}>
        <SearchCarTiresize />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCarModelSelection,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 車種から選ぶ`}>
        <CarTypeSelector type="model" />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCarModelTireWheelSelection,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 車種から選ぶ`}>
        <CarTypeSelector type="wheel" />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCarModelProductCode,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品コードから選ぶ`}>
        <MstCarModelProductCode />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCarWheelProductCode,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品コードから選ぶ`}>
        <WheelCode />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstProcFee,
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤセットを選ぶ`}>
        <ProcFeeList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstConfirmProcFee,
    element: (
      <RouteTitle title={`${BASE_TITLE} - お見積表`}>
        <ProcFeeConfirm />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstSelectedTireProcFee,
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤセットを選ぶ`}>
        <SetTireProcFeeList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstWheelTireProcFee,
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤを選ぶ`}>
        <WheelTireProcFeeList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCarModelResult,
    element: (
      <RouteTitle title={`${BASE_TITLE} - お見積表`}>
        <MstCarModelEstResult />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstYhCompany,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 企業`}>
        <YHCompanyList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCategory,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 分類マスタ`}>
        <MstCategoryList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstVendor,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 仕入先一覧`}>
        <MstVendorList />
      </RouteTitle>
    ),
  },
  {
    path: routes.forbidden,
    element: (
      <RouteTitle title={`${BASE_TITLE} - Forbidden`}>
        <Forbidden />
      </RouteTitle>
    ),
  },
  {
    path: routes.mst4BundlePriceTwSetSetting,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 4本特価タイヤ設定`}>
        <FourBundlePriceList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstCsv,
    element: (
      <RouteTitle title={`${BASE_TITLE} - CSVアップロード`}>
        <CsvList />
      </RouteTitle>
    ),
  },
  // { path: routes.mstWheelBrandList, element: <RouteTitle title={`${BASE_TITLE} - ブランド詳細`}><WheelBrand /></RouteTitle> },
  {
    path: routes.mstWheelPriceSetting,
    element: (
      <RouteTitle title={`${BASE_TITLE} - ホイール価格設定`}>
        {userType === USER_TYPE_LIST.headquarters.value ? <WheelBrand /> : <WheelPriceSetting />}
      </RouteTitle>
    ),
  },
  {
    path: routes.mstWinterWheelPriceSetting,
    element: (
      <RouteTitle title={`${BASE_TITLE} - ホイール価格設定`}>
        <WinterWheelPriceSetting role={userType} />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstWheel,
    element: (
      <RouteTitle title={`${BASE_TITLE} - ホイール一覧`}>
        <WheelList />
      </RouteTitle>
    ),
  },
  {
    path: routes.mstOption,
    element: (
      <RouteTitle title={`${BASE_TITLE} - 商品オプション`}>
        <MstOption />
      </RouteTitle>
    ),
  },
  {
    path: "/tire-report",
    element: (
      <RouteTitle title={`${BASE_TITLE} - タイヤレポート`}>
        <TireReportPage />
      </RouteTitle>
    ),
  },
  {
    path: "/system-usage",
    element: (
      <RouteTitle title={`${BASE_TITLE} - システム利用状況`}>
        <SystemUsagePage />
      </RouteTitle>
    ),
  },
  {
    path: "/sms-report",
    element: (
      <RouteTitle title={`${BASE_TITLE} - SMS送信件数`}>
        <SMSPage />
      </RouteTitle>
    ),
  },
  {
    path: "/bo-error-report",
    element: (
      <RouteTitle title={`${BASE_TITLE} - 店舗BO連携エラー`}>
        <BOErrorPage />
      </RouteTitle>
    ),
  },
  {
    path: "/mst-maintenance",
    element: (
      <RouteTitle title={`${BASE_TITLE} - 汎用マスタメンテナンス`}>
        <MstMain />
      </RouteTitle>
    ),
  },
  {
    path: routes.notFound,
    element: (
      <RouteTitle title={`${BASE_TITLE} - Not Found`}>
        <NotFound />
      </RouteTitle>
    ),
  },
];
const mappedRoutes = (userType: keyof typeof USER_TYPE): JSX.Element[] =>
  getRoleBasedRoutes(userType).map((route) => (
    <Route key={route.path} element={<RoleBasedRoute path={route.path} />}>
      <Route path={route.path} element={route.element} />
    </Route>
  ));

/**
 * パスワードリセット後のリダイレクト
 */
function ResetPasswordRedirect() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return token ? (
    <Navigate to={`${routes.changePassword}?password_reset_token=${token}`} />
  ) : (
    <Navigate to={routes.notFound} />
  );
}

/**
 * AppのRoutesコンポーネント
 */
function AppRoutes() {
  const userType = getUserType() as keyof typeof USER_TYPE;

  return (
    <Routes>
      <Route
        path="login"
        element={
          <RouteTitle title={`${BASE_TITLE} - ログイン`}>
            <Login />
          </RouteTitle>
        }
      />
      <Route
        path={routes.resetPassword}
        element={
          <RouteTitle title={`${BASE_TITLE} - パスワード変更`}>
            <ResetPasswordRedirect />
          </RouteTitle>
        }
      />
      <Route
        path={routes.changePassword}
        element={
          <RouteTitle title={`${BASE_TITLE} - パスワード再発行`}>
            <ChangePassword />
          </RouteTitle>
        }
      />
      <Route
        path={routes.forgotPassword}
        element={
          <RouteTitle title={`${BASE_TITLE} - パスワードを忘れた`}>
            <ForgotPassword />
          </RouteTitle>
        }
      />
      <Route path={routes.changelog} element={<Changelog />} />
      <Route path={routes.updates} element={<Changelog />} />
      <Route
        path={routes.quotation}
        element={
          <RouteTitle title={`${BASE_TITLE} - QRコード`}>
            <ViewQuotationPdf />
          </RouteTitle>
        }
      />
      <Route
        path={routes.mstCarModelPdf}
        element={
          <RouteTitle title={`${BASE_TITLE} - 見積確定`}>
            <MstCarModelEstResultPdf />
          </RouteTitle>
        }
      />
      <Route
        path={routes.mstWheelTireEstResultPdf}
        element={
          <RouteTitle title={`${BASE_TITLE} - 見積確定`}>
            <WheelTireEstResultPdf />
          </RouteTitle>
        }
      />
      <Route
        path={routes.invalidRoute}
        element={
          <RouteTitle title={`${BASE_TITLE} - 該当のページは存在しません`}>
            <InvalidRoutePage />
          </RouteTitle>
        }
      />
      <Route
        path="/joyful/*"
        element={
          <ThemeProvider theme={theme}>
            <HeaderFooterRoutes
              elements={[
                <Route
                  path="/"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - ジョイフル用設定`}>
                      <JoyfulSetting />
                    </RouteTitle>
                  }
                />,
                <Route
                  path="product-setting"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - ジョイフル用商品設定`}>
                      <ProductSetting />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </ThemeProvider>
        }
      />
      <Route
        path="/supplier-warehouse/*"
        element={
          <ThemeProvider theme={theme}>
            <HeaderFooterRoutes
              elements={[
                <Route
                  path="/"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 仕入先用近隣・センター倉庫登録画面`}>
                      <WarehouseRegister />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </ThemeProvider>
        }
      />
      {/* <Route path="/home" element={<Home />} /> */}
      <Route
        path="/orders/*"
        element={
          <ThemeProvider theme={theme}>
            <HeaderFooterRoutes
              elements={[
                <Route
                  path=""
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 注文商品一覧`}>
                      <OrderList />
                    </RouteTitle>
                  }
                />,
                <Route
                  path="store"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 注文商品一覧`}>
                      <OrderStoreList />
                    </RouteTitle>
                  }
                />,
                <Route
                  path="supplier"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 受注商品一覧`}>
                      <OrderReceive />
                    </RouteTitle>
                  }
                />,
                <Route
                  path="weekly-replenishment"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 週次補充CSV`}>
                      <LargeCsv />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </ThemeProvider>
        }
      />
      <Route
        path="/order/*"
        element={
          <ThemeProvider theme={theme}>
            <HeaderFooterRoutes
              elements={[
                <Route
                  path="form"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 注文作成・編集`}>
                      <OrderCheckForm />
                    </RouteTitle>
                  }
                />,
                // ToDo: 一時的に店舗注文をコメントアウト
                // <Route
                //   path="store/form"
                //   element={
                //     <RouteTitle title={`${BASE_TITLE} - 注文作成`}>
                //       <OrderStoreCheckForm />
                //     </RouteTitle>
                //   }
                // />,
                <Route
                  path="rpa"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - RPA用CSVアップロード`}>
                      <RPACsvUpload />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </ThemeProvider>
        }
      />
      <Route path="/*" element={<HeaderFooterRoutes elements={mappedRoutes(userType)} />} />
      <Route
        path="/stock/*"
        element={
          <ThemeProvider theme={theme}>
            <HeaderFooterRoutes
              elements={[
                <Route
                  key="client_stock"
                  path="client"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫一覧`}>
                      <ClientStockList />
                    </RouteTitle>
                  }
                />,
                <Route
                  key="main"
                  path="main"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫一覧`}>
                      <StockList />
                    </RouteTitle>
                  }
                />,
                <Route
                  key="store"
                  path="store"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫一覧`}>
                      <StoreStockList />
                    </RouteTitle>
                  }
                />,
                <Route
                  key="company"
                  path="company"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫一覧`}>
                      <CompanyStockList />
                    </RouteTitle>
                  }
                />,
                <Route
                  key="supplier"
                  path="supplier"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫一覧`}>
                      <SupplierStockList />
                    </RouteTitle>
                  }
                />,
                <Route
                  key="main_update"
                  path="main/update"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - 在庫更新`}>
                      <ProductStock />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </ThemeProvider>
        }
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <HeaderFooterRoutes
              elements={[
                <Route
                  key="home"
                  path="/"
                  element={
                    <RouteTitle title={`${BASE_TITLE} - トップページ`}>
                      <Home />
                    </RouteTitle>
                  }
                />,
              ]}
            />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

export default AppRoutes;
